import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';

/**
 * Form alanı standardı (docs/05 §6):
 * etiket · alan · alan altında kırmızı hata metni · isteğe bağlı yardım metni.
 * Hata varsa `aria-invalid` ve `aria-describedby` bağlanır (erişilebilirlik).
 */

const controlBase =
  'block w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm ' +
  'transition placeholder:text-slate-400 disabled:cursor-not-allowed disabled:bg-slate-50';

const controlNormal = 'border-slate-300 focus:border-primary-600';
const controlError = 'border-red-500 focus:border-red-600';

interface FieldWrapperProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
  htmlFor: string;
  children: ReactNode;
  className?: string;
}

export function FieldWrapper({
  label,
  error,
  hint,
  required,
  htmlFor,
  children,
  className,
}: FieldWrapperProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700">
        {label}
        {required && (
          <span className="ml-1 text-red-600" aria-label={t('common.required')}>
            *
          </span>
        )}
      </label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} className="text-sm text-red-600">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${htmlFor}-hint`} className="text-sm text-slate-500">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

interface BaseFieldProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  wrapperClassName?: string;
}

type InputFieldProps = BaseFieldProps & InputHTMLAttributes<HTMLInputElement>;

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(function InputField(
  { label, error, hint, wrapperClassName, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <FieldWrapper
      label={label}
      error={error}
      hint={hint}
      required={props.required}
      htmlFor={fieldId}
      className={wrapperClassName}
    >
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        className={cn(controlBase, error ? controlError : controlNormal, className)}
        {...props}
      />
    </FieldWrapper>
  );
});

type SelectFieldProps = BaseFieldProps &
  SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode };

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, wrapperClassName, className, id, children, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <FieldWrapper
      label={label}
      error={error}
      hint={hint}
      required={props.required}
      htmlFor={fieldId}
      className={wrapperClassName}
    >
      <select
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        className={cn(controlBase, error ? controlError : controlNormal, className)}
        {...props}
      >
        {children}
      </select>
    </FieldWrapper>
  );
});

type TextareaFieldProps = BaseFieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField({ label, error, hint, wrapperClassName, className, id, ...props }, ref) {
    const generatedId = useId();
    const fieldId = id ?? generatedId;

    return (
      <FieldWrapper
        label={label}
        error={error}
        hint={hint}
        required={props.required}
        htmlFor={fieldId}
        className={wrapperClassName}
      >
        <textarea
          ref={ref}
          id={fieldId}
          rows={props.rows ?? 4}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          className={cn(controlBase, error ? controlError : controlNormal, className)}
          {...props}
        />
      </FieldWrapper>
    );
  },
);

type CheckboxFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: ReactNode;
  error?: string | undefined;
};

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(
  function CheckboxField({ label, error, className, id, ...props }, ref) {
    const generatedId = useId();
    const fieldId = id ?? generatedId;

    return (
      <div className="space-y-1.5">
        <div className="flex items-start gap-2.5">
          <input
            ref={ref}
            id={fieldId}
            type="checkbox"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${fieldId}-error` : undefined}
            className={cn(
              'mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-900',
              error && 'border-red-500',
              className,
            )}
            {...props}
          />
          <label htmlFor={fieldId} className="text-sm text-slate-700">
            {label}
          </label>
        </div>
        {error && (
          <p id={`${fieldId}-error`} className="text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  },
);
